/**
 * V9.11 — Contribuer un article.
 *
 * Page accessible à tout utilisateur connecté.
 * - Si pas connecté → redirige vers /login?next=/contribuer.
 * - Formulaire simple : titre, résumé, contenu Markdown, URL couverture, discipline.
 * - Boutons : « Enregistrer brouillon » (status=pending sans submitted_at) et
 *   « Soumettre à validation » (status=pending + submitted_at).
 * - Aperçu Markdown en direct.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ArrowLeft, FileText, Info, Loader2, Send, Save, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { showError } from "@/lib/errors";
import { MarkdownRenderer } from "@/components/MarkdownRenderer";
import RequireAuth from "@/components/RequireAuth";

function ContributeFormInner() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({
    title: "",
    summary: "",
    body_md: "",
    cover_url: "",
    discipline: "",
  });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);

  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async ({ submit }) => {
    if (!form.title.trim()) {
      toast.error("Le titre est obligatoire.");
      return;
    }
    if (form.body_md.trim().length < 30) {
      toast.error("Le contenu doit faire au moins 30 caractères.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/contrib/articles", { ...form, submit });
      toast.success(
        submit
          ? "Article soumis à modération — il apparaîtra dans les actualités après validation."
          : "Brouillon enregistré dans « Mes articles »."
      );
      navigate(`/mes-articles/${data.id}`);
    } catch (e) {
      showError(e, { fallback: "Impossible d'enregistrer l'article." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Link to="/" className="hover:underline">Accueil</Link>
            <span>›</span>
            <Link to="/actualites" className="hover:underline">Actualités</Link>
            <span>›</span>
            <span>Contribuer</span>
          </div>
          <h1 className="font-display font-bold text-2xl sm:text-3xl mt-1" data-testid="contribute-title">
            Publier un article
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Partage un récit, un résultat, un témoignage. Après validation par un admin, ton article apparaîtra dans le fil <strong>Actualités</strong>.
          </p>
        </div>
        <Link to="/mes-articles">
          <Button variant="outline" size="sm" data-testid="link-mine-articles">
            <FileText className="h-4 w-4 mr-1.5" />
            Mes articles
          </Button>
        </Link>
      </div>

      <Alert className="mb-5 border-[hsl(var(--aito-lagoon))]/30 bg-[hsl(var(--aito-lagoon))]/5">
        <Info className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
        <AlertTitle>Format des images</AlertTitle>
        <AlertDescription className="text-xs">
          AITO.PF n'héberge pas encore tes fichiers. Pour les images, utilise <strong>l'URL publique</strong> d'une image déjà en ligne
          (Facebook, Instagram, Google Drive partagé, Imgur, etc.). Tu peux insérer une image dans le corps de l'article avec la syntaxe :
          {" "}<code>![légende](https://exemple.com/image.jpg)</code>.
        </AlertDescription>
      </Alert>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-4 sm:p-5 space-y-4">
          <div>
            <label className="text-xs font-medium">Titre <span className="text-destructive">*</span></label>
            <Input
              data-testid="contribute-title-input"
              value={form.title}
              maxLength={180}
              onChange={(e) => upd({ title: e.target.value })}
              placeholder="Un titre court et clair"
            />
            <div className="text-[10px] text-muted-foreground mt-0.5 text-right">{form.title.length}/180</div>
          </div>
          <div>
            <label className="text-xs font-medium">Résumé court (optionnel)</label>
            <Textarea
              data-testid="contribute-summary-input"
              value={form.summary}
              maxLength={280}
              rows={2}
              onChange={(e) => upd({ summary: e.target.value })}
              placeholder="Un résumé en 1-2 phrases (~280 caractères)"
            />
            <div className="text-[10px] text-muted-foreground mt-0.5 text-right">{form.summary.length}/280</div>
          </div>
          <div>
            <label className="text-xs font-medium">URL image de couverture (optionnel)</label>
            <Input
              data-testid="contribute-cover-input"
              value={form.cover_url}
              onChange={(e) => upd({ cover_url: e.target.value })}
              placeholder="https://exemple.com/ma-photo.jpg"
            />
            <div className="text-[10px] text-muted-foreground mt-0.5">
              Astuce&nbsp;: sur ton image en ligne, fais <em>clic droit → « Copier l'adresse de l'image »</em>.
            </div>
          </div>
          <div>
            <label className="text-xs font-medium">Discipline (optionnel)</label>
            <Input
              data-testid="contribute-discipline-input"
              value={form.discipline}
              onChange={(e) => upd({ discipline: e.target.value })}
              placeholder="surf, jiu-jitsu, va'a..."
            />
          </div>
          <div>
            <label className="text-xs font-medium">Contenu <span className="text-destructive">*</span></label>
            <Textarea
              data-testid="contribute-body-input"
              value={form.body_md}
              maxLength={30000}
              rows={14}
              onChange={(e) => upd({ body_md: e.target.value })}
              placeholder={"# Titre principal\n\nUn paragraphe d'introduction.\n\n## Sous-titre\n\nDu texte en **gras**, en *italique*.\n\n- Liste à puce\n- Élément 2\n\n![Légende](https://exemple.com/image.jpg)\n\n> Citation marquante.\n\n[Lien externe](https://example.com)"}
              className="font-mono text-xs"
            />
            <div className="flex justify-between mt-1">
              <Badge variant="outline" className="text-[9px]">Markdown léger : **gras** *italique* # titre - liste &gt; citation [lien](url) ![image](url)</Badge>
              <span className="text-[10px] text-muted-foreground">{form.body_md.length}/30000</span>
            </div>
          </div>

          <div className="flex items-center gap-2 pt-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => setPreview((p) => !p)}
              data-testid="toggle-preview-btn"
            >
              <Sparkles className="h-4 w-4 mr-1.5" />
              {preview ? "Masquer l'aperçu" : "Voir l'aperçu"}
            </Button>
            <div className="flex-1" />
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => save({ submit: false })}
              data-testid="save-draft-btn"
            >
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
              Enregistrer brouillon
            </Button>
            <Button
              className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]"
              disabled={busy}
              onClick={() => save({ submit: true })}
              data-testid="submit-article-btn"
            >
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
              Soumettre à validation
            </Button>
          </div>
        </Card>

        <Card className="p-4 sm:p-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
            Aperçu (rendu après publication)
          </div>
          {form.cover_url && (
            <img
              src={form.cover_url}
              alt={form.title}
              className="w-full max-h-64 object-cover rounded-md mb-3"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          )}
          {form.title && <h1 className="font-display font-bold text-2xl mb-1 break-words">{form.title}</h1>}
          {form.summary && <p className="text-sm text-muted-foreground italic mb-4">{form.summary}</p>}
          <MarkdownRenderer>{form.body_md || "_Aucun contenu pour l'instant._"}</MarkdownRenderer>
          <div className="mt-4 pt-3 border-t text-[11px] text-muted-foreground">
            Par <strong>{user?.name || "—"}</strong> · sera publié après validation par l'équipe AITO.PF.
          </div>
        </Card>
      </div>
    </div>
  );
}

export default function ContributeArticlePage() {
  return (
    <RequireAuth>
      <ContributeFormInner />
    </RequireAuth>
  );
}
